import React, { useCallback, useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import { jsPDF } from "jspdf";
import {
  Download,
  FileUp,
  Grid3X3,
  RefreshCw,
  ScanLine,
  Trash2,
} from "lucide-react";

/*
 * PdfStickerGridDetector_KissCut.jsx
 *
 * What this version does:
 * 1. Reads the ORIGINAL PDF page dimensions from PDF.js.
 * 2. Detects the same regular grid used by the jsPDF generator.
 * 3. Detects the actual artwork/border contour inside EVERY detected cell.
 * 4. Uses the same contour pipeline as StickerKissCutStudio:
 *      background/alpha mask -> remove noise -> join -> contour
 *      -> smoothing -> true geometric offset.
 * 5. Downloads a NEW PDF with the ORIGINAL rendered artwork unchanged
 *    and MAGENTA vector kiss-cut lines drawn on top.
 *
 * Important:
 * - The kiss-cut line is NOT painted into the artwork image.
 * - The downloaded PDF keeps the exact source page width/height.
 * - Grid rectangles are NOT used as kiss cuts. The detected border contour is.
 */

const MAGENTA = [255, 0, 255];
const WORK_MAX_DIM = 560;

// Same real-world page used by the current jsPDF generator for acustom landscape.
// The component does NOT hardcode this for export; it reads the uploaded PDF.
const KNOWN_JSPDF_SHEET = {
  widthMm: 460,
  heightMm: 305,
  cols: 5,
  rows: 5,
  gridWidthMm: 89,
  gridHeightMm: 57,
};

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

function smoothProjection(values, radius = 3) {
  if (!values.length) return values;
  const out = new Array(values.length).fill(0);

  for (let i = 0; i < values.length; i++) {
    let sum = 0;
    let count = 0;
    for (
      let j = Math.max(0, i - radius);
      j <= Math.min(values.length - 1, i + radius);
      j++
    ) {
      sum += values[j];
      count++;
    }
    out[i] = sum / count;
  }

  return out;
}

function normalizeProjection(values) {
  const max = Math.max(...values, 1);
  return values.map((v) => v / max);
}

function getEdgeProjections(canvas) {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const { width, height } = canvas;
  const data = ctx.getImageData(0, 0, width, height).data;

  const vertical = new Float64Array(width);
  const horizontal = new Float64Array(height);
  const step = Math.max(1, Math.floor(Math.min(width, height) / 900));

  for (let x = 1; x < width; x += step) {
    let sum = 0;
    let count = 0;

    for (let y = 0; y < height; y += step) {
      const a = (y * width + x - 1) * 4;
      const b = (y * width + x) * 4;

      sum +=
        Math.abs(data[a] - data[b]) +
        Math.abs(data[a + 1] - data[b + 1]) +
        Math.abs(data[a + 2] - data[b + 2]);
      count++;
    }

    vertical[x] = count ? sum / count : 0;
  }

  for (let y = 1; y < height; y += step) {
    let sum = 0;
    let count = 0;

    for (let x = 0; x < width; x += step) {
      const a = ((y - 1) * width + x) * 4;
      const b = (y * width + x) * 4;

      sum +=
        Math.abs(data[a] - data[b]) +
        Math.abs(data[a + 1] - data[b + 1]) +
        Math.abs(data[a + 2] - data[b + 2]);
      count++;
    }

    horizontal[y] = count ? sum / count : 0;
  }

  return {
    vertical: normalizeProjection(smoothProjection(Array.from(vertical), 4)),
    horizontal: normalizeProjection(smoothProjection(Array.from(horizontal), 4)),
  };
}

function pickSeparatorLines(projection, expectedCount, minGapPx) {
  const n = projection.length;
  if (n < 20 || expectedCount <= 1) return [];

  const margin = Math.max(5, Math.floor(n * 0.015));
  const candidates = [];

  for (let i = margin; i < n - margin; i++) {
    const v = projection[i];
    if (v < 0.15) continue;

    const left = projection[i - 2] ?? v;
    const right = projection[i + 2] ?? v;

    if (v >= left && v >= right) {
      candidates.push({ pos: i, score: v });
    }
  }

  candidates.sort((a, b) => b.score - a.score);

  const selected = [];

  for (const c of candidates) {
    if (selected.every((s) => Math.abs(s.pos - c.pos) >= minGapPx)) {
      selected.push(c);
      if (selected.length >= expectedCount - 1) break;
    }
  }

  selected.sort((a, b) => a.pos - b.pos);
  return selected.map((x) => x.pos);
}

function fallbackEqualLines(length, count) {
  const result = [];
  for (let i = 1; i < count; i++) {
    result.push(Math.round((length * i) / count));
  }
  return result;
}

function inferGrid(canvas, rowsHint, colsHint) {
  const { vertical, horizontal } = getEdgeProjections(canvas);

  const minColGap = Math.max(
    20,
    Math.floor(canvas.width / Math.max(colsHint * 3, 12))
  );
  const minRowGap = Math.max(
    20,
    Math.floor(canvas.height / Math.max(rowsHint * 3, 12))
  );

  let xLines = pickSeparatorLines(vertical, colsHint, minColGap);
  let yLines = pickSeparatorLines(horizontal, rowsHint, minRowGap);

  if (xLines.length !== colsHint - 1) {
    xLines = fallbackEqualLines(canvas.width, colsHint);
  }

  if (yLines.length !== rowsHint - 1) {
    yLines = fallbackEqualLines(canvas.height, rowsHint);
  }

  const xs = [0, ...xLines, canvas.width];
  const ys = [0, ...yLines, canvas.height];

  const cells = [];

  for (let row = 0; row < rowsHint; row++) {
    for (let col = 0; col < colsHint; col++) {
      const x = xs[col];
      const y = ys[row];
      const width = xs[col + 1] - x;
      const height = ys[row + 1] - y;

      if (width > 10 && height > 10) {
        cells.push({
          id: `${row}-${col}`,
          row,
          col,
          x,
          y,
          width,
          height,
        });
      }
    }
  }

  return {
    rows: rowsHint,
    cols: colsHint,
    xLines,
    yLines,
    cells,
  };
}

/* =========================================================================
   StickerKissCutStudio contour pipeline
   ========================================================================= */

function loadWorkingCanvasFromCanvas(sourceCanvas) {
  const scale = Math.min(
    1,
    WORK_MAX_DIM / Math.max(sourceCanvas.width, sourceCanvas.height)
  );

  const w = Math.max(8, Math.round(sourceCanvas.width * scale));
  const h = Math.max(8, Math.round(sourceCanvas.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(sourceCanvas, 0, 0, w, h);

  return {
    imageData: ctx.getImageData(0, 0, w, h),
    w,
    h,
    scale,
  };
}

function detectHasAlpha(imageData) {
  const d = imageData.data;

  for (let i = 3; i < d.length; i += 4) {
    if (d[i] < 250) return true;
  }

  return false;
}

function buildAlphaMask(imageData, w, h, alphaThreshold) {
  const d = imageData.data;
  const mask = new Uint8Array(w * h);

  for (let i = 3, p = 0; p < w * h; i += 4, p++) {
    mask[p] = d[i] > alphaThreshold ? 1 : 0;
  }

  return mask;
}

function buildBackgroundMask(imageData, w, h, threshold) {
  const d = imageData.data;
  const corners = [
    [0, 0],
    [w - 1, 0],
    [0, h - 1],
    [w - 1, h - 1],
  ];

  let r = 0;
  let g = 0;
  let b = 0;

  corners.forEach(([x, y]) => {
    const idx = (y * w + x) * 4;
    r += d[idx];
    g += d[idx + 1];
    b += d[idx + 2];
  });

  r /= 4;
  g /= 4;
  b /= 4;

  const colorDist = (idx) => {
    const dr = d[idx] - r;
    const dg = d[idx + 1] - g;
    const db = d[idx + 2] - b;
    return Math.sqrt(dr * dr + dg * dg + db * db);
  };

  const bg = new Uint8Array(w * h);
  const visited = new Uint8Array(w * h);
  const stack = [];

  for (let x = 0; x < w; x++) {
    stack.push(x, 0, x, h - 1);
  }

  for (let y = 0; y < h; y++) {
    stack.push(0, y, w - 1, y);
  }

  while (stack.length) {
    const y = stack.pop();
    const x = stack.pop();

    if (x < 0 || y < 0 || x >= w || y >= h) continue;

    const p = y * w + x;

    if (visited[p]) continue;
    visited[p] = 1;

    if (colorDist(p * 4) <= threshold) {
      bg[p] = 1;

      stack.push(
        x + 1,
        y,
        x - 1,
        y,
        x,
        y + 1,
        x,
        y - 1
      );
    }
  }

  const mask = new Uint8Array(w * h);

  for (let p = 0; p < w * h; p++) {
    mask[p] = bg[p] ? 0 : 1;
  }

  return mask;
}

function labelComponents(mask, w, h) {
  const labels = new Int32Array(w * h).fill(-1);
  const areas = [];
  let current = 0;
  const stack = [];

  for (let start = 0; start < w * h; start++) {
    if (mask[start] !== 1 || labels[start] !== -1) continue;

    let area = 0;

    stack.push(start);
    labels[start] = current;

    while (stack.length) {
      const p = stack.pop();
      area++;

      const x = p % w;
      const y = (p / w) | 0;

      const neighbours = [];

      if (x + 1 < w) neighbours.push(p + 1);
      if (x - 1 >= 0) neighbours.push(p - 1);
      if (y + 1 < h) neighbours.push(p + w);
      if (y - 1 >= 0) neighbours.push(p - w);

      for (const np of neighbours) {
        if (mask[np] === 1 && labels[np] === -1) {
          labels[np] = current;
          stack.push(np);
        }
      }
    }

    areas.push(area);
    current++;
  }

  return { labels, areas, count: current };
}

function removeSmallComponents(mask, w, h, minAreaPx) {
  const { labels, areas, count } = labelComponents(mask, w, h);
  const keep = new Uint8Array(count);

  for (let i = 0; i < count; i++) {
    keep[i] = areas[i] >= minAreaPx ? 1 : 0;
  }

  const out = new Uint8Array(w * h);

  for (let p = 0; p < w * h; p++) {
    const l = labels[p];
    if (l >= 0 && keep[l]) out[p] = 1;
  }

  return out;
}

function dilate(mask, w, h, radius) {
  if (radius <= 0) return mask.slice();

  const out = new Uint8Array(w * h);
  const r2 = radius * radius;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (mask[y * w + x] !== 1) continue;

      for (let dy = -radius; dy <= radius; dy++) {
        const ny = y + dy;
        if (ny < 0 || ny >= h) continue;

        for (let dx = -radius; dx <= radius; dx++) {
          if (dx * dx + dy * dy > r2) continue;

          const nx = x + dx;
          if (nx < 0 || nx >= w) continue;

          out[ny * w + nx] = 1;
        }
      }
    }
  }

  return out;
}

function erode(mask, w, h, radius) {
  if (radius <= 0) return mask.slice();

  const out = new Uint8Array(w * h);
  const r2 = radius * radius;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let keep = 1;

      for (let dy = -radius; dy <= radius && keep; dy++) {
        const ny = y + dy;

        for (let dx = -radius; dx <= radius; dx++) {
          if (dx * dx + dy * dy > r2) continue;

          const nx = x + dx;

          if (
            nx < 0 ||
            ny < 0 ||
            nx >= w ||
            ny >= h ||
            mask[ny * w + nx] !== 1
          ) {
            keep = 0;
            break;
          }
        }
      }

      out[y * w + x] = keep;
    }
  }

  return out;
}

function morphClose(mask, w, h, radius) {
  return erode(dilate(mask, w, h, radius), w, h, radius);
}

const NBR = [
  [0, -1],
  [1, -1],
  [1, 0],
  [1, 1],
  [0, 1],
  [-1, 1],
  [-1, 0],
  [-1, -1],
];

function offsetIndex(dx, dy) {
  for (let i = 0; i < 8; i++) {
    if (NBR[i][0] === dx && NBR[i][1] === dy) return i;
  }
  return 6;
}

function traceBoundary(mask, w, h, sx, sy) {
  const isFg = (x, y) =>
    x >= 0 &&
    y >= 0 &&
    x < w &&
    y < h &&
    mask[y * w + x] === 1;

  const start = [sx, sy];
  let P = start;
  let B = [sx - 1, sy];
  const contour = [P];
  const maxSteps = w * h * 4;

  for (let steps = 0; steps < maxSteps; steps++) {
    const startIdx = offsetIndex(B[0] - P[0], B[1] - P[1]);
    let found = null;
    let prevBg = B;

    for (let i = 1; i <= 8; i++) {
      const idx = (startIdx + i) % 8;
      const [ox, oy] = NBR[idx];
      const nx = P[0] + ox;
      const ny = P[1] + oy;

      if (isFg(nx, ny)) {
        found = [nx, ny];
        break;
      }

      prevBg = [nx, ny];
    }

    if (!found) break;

    B = prevBg;
    P = found;
    contour.push(P);

    if (steps > 0 && P[0] === start[0] && P[1] === start[1]) {
      break;
    }
  }

  return contour;
}

function findLargestComponentSeed(mask, w, h) {
  const { labels, areas, count } = labelComponents(mask, w, h);

  if (count === 0) return null;

  let best = 0;

  for (let i = 1; i < count; i++) {
    if (areas[i] > areas[best]) best = i;
  }

  for (let p = 0; p < w * h; p++) {
    if (labels[p] === best) {
      return {
        x: p % w,
        y: (p / w) | 0,
        area: areas[best],
      };
    }
  }

  return null;
}

function perpDist(pt, a, b) {
  const [x, y] = pt;
  const [x1, y1] = a;
  const [x2, y2] = b;

  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);

  if (len === 0) return Math.hypot(x - x1, y - y1);

  const t =
    ((x - x1) * dx + (y - y1) * dy) /
    (len * len);

  const px = x1 + t * dx;
  const py = y1 + t * dy;

  return Math.hypot(x - px, y - py);
}

function douglasPeucker(points, epsilon) {
  if (points.length < 3) return points.slice();

  let maxDist = 0;
  let index = 0;

  for (let i = 1; i < points.length - 1; i++) {
    const d = perpDist(points[i], points[0], points[points.length - 1]);

    if (d > maxDist) {
      maxDist = d;
      index = i;
    }
  }

  if (maxDist > epsilon) {
    const left = douglasPeucker(points.slice(0, index + 1), epsilon);
    const right = douglasPeucker(points.slice(index), epsilon);

    return left.slice(0, -1).concat(right);
  }

  return [points[0], points[points.length - 1]];
}

function simplifyClosed(points, epsilon) {
  if (points.length < 4) return points.slice();

  const closed =
    points[0][0] === points[points.length - 1][0] &&
    points[0][1] === points[points.length - 1][1]
      ? points
      : points.concat([points[0]]);

  const simplified = douglasPeucker(closed, epsilon);

  if (simplified.length > 1) {
    const f = simplified[0];
    const l = simplified[simplified.length - 1];

    if (f[0] === l[0] && f[1] === l[1]) {
      simplified.pop();
    }
  }

  return simplified;
}

function signedArea(points) {
  let a = 0;

  for (let i = 0; i < points.length; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % points.length];

    a += x1 * y2 - x2 * y1;
  }

  return a / 2;
}

function lineIntersect(p1, p2, p3, p4) {
  const [x1, y1] = p1;
  const [x2, y2] = p2;
  const [x3, y3] = p3;
  const [x4, y4] = p4;

  const denom =
    (x1 - x2) * (y3 - y4) -
    (y1 - y2) * (x3 - x4);

  if (Math.abs(denom) < 1e-9) return null;

  const a = x1 * y2 - y1 * x2;
  const b = x3 * y4 - y3 * x4;

  return [
    (a * (x3 - x4) - (x1 - x2) * b) / denom,
    (a * (y3 - y4) - (y1 - y2) * b) / denom,
  ];
}

function offsetPolygon(points, distance) {
  const n = points.length;

  if (n < 3 || distance === 0) return points.slice();

  const sign = signedArea(points) >= 0 ? 1 : -1;
  const edges = [];

  for (let i = 0; i < n; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % n];

    let dx = p2[0] - p1[0];
    let dy = p2[1] - p1[1];

    const len = Math.hypot(dx, dy) || 1;

    dx /= len;
    dy /= len;

    const nx = dy * sign;
    const ny = -dx * sign;

    edges.push({
      p1: [p1[0] + nx * distance, p1[1] + ny * distance],
      p2: [p2[0] + nx * distance, p2[1] + ny * distance],
    });
  }

  const miterLimit = Math.abs(distance) * 1.5;
  const result = [];

  for (let i = 0; i < n; i++) {
    const eA = edges[(i - 1 + n) % n];
    const eB = edges[i];

    const ip = lineIntersect(eA.p1, eA.p2, eB.p1, eB.p2);
    const orig = points[i];

    if (
      ip &&
      Math.hypot(ip[0] - orig[0], ip[1] - orig[1]) <= miterLimit
    ) {
      result.push(ip);
    } else {
      result.push([
        (eA.p2[0] + eB.p1[0]) / 2,
        (eA.p2[1] + eB.p1[1]) / 2,
      ]);
    }
  }

  return result;
}

/*
 * Detect the kiss-cut contour of ONE detected grid cell.
 *
 * The cell is first placed on a small white pad. This is important when the
 * border artwork touches the detected grid edge.
 */
function detectCellKissCut(cellCanvas, params) {
  const pad = Math.max(
    6,
    Math.round(Math.min(cellCanvas.width, cellCanvas.height) * 0.02)
  );

  const padded = document.createElement("canvas");
  padded.width = cellCanvas.width + pad * 2;
  padded.height = cellCanvas.height + pad * 2;

  const pctx = padded.getContext("2d", { willReadFrequently: true });
  pctx.fillStyle = "#ffffff";
  pctx.fillRect(0, 0, padded.width, padded.height);
  pctx.drawImage(cellCanvas, pad, pad);

  const { imageData, w, h, scale } =
    loadWorkingCanvasFromCanvas(padded);

  const hasAlpha = detectHasAlpha(imageData);

  let mask = hasAlpha
    ? buildAlphaMask(imageData, w, h, 20)
    : buildBackgroundMask(
        imageData,
        w,
        h,
        params.bgThreshold
      );

  const minAreaPx = Math.max(
    4,
    Math.round(w * h * 0.00025)
  );

  mask = removeSmallComponents(
    mask,
    w,
    h,
    minAreaPx
  );

  if (params.joinDistance > 0) {
    mask = morphClose(
      mask,
      w,
      h,
      params.joinDistance
    );
  }

  const seed = findLargestComponentSeed(mask, w, h);

  if (!seed) {
    return {
      ok: false,
      points: [],
      reason: "No artwork/border contour found",
    };
  }

  const raw = traceBoundary(
    mask,
    w,
    h,
    seed.x,
    seed.y
  );

  if (raw.length < 3) {
    return {
      ok: false,
      points: [],
      reason: "Contour was too small",
    };
  }

  const epsilon = Math.max(
    0.4,
    params.smoothing * 1.1
  );

  let simplified = simplifyClosed(
    raw,
    epsilon
  );

  if (simplified.length < 3) {
    simplified = raw;
  }

  /*
   * params.offsetMm is converted with the same DPI model as
   * StickerKissCutStudio. The source PDF itself is NOT changed.
   */
  const offsetPxWork =
    ((params.offsetMm / 25.4) * params.dpi) *
    scale;

  const offset = offsetPolygon(
    simplified,
    offsetPxWork
  );

  /*
   * Convert working-canvas coordinates back to padded-cell pixels.
   * Then remove the white pad so coordinates match the original cell.
   */
  const points = offset.map(([x, y]) => [
    x / scale - pad,
    y / scale - pad,
  ]);

  const valid = points.every(
    ([x, y]) =>
      Number.isFinite(x) &&
      Number.isFinite(y)
  );

  if (!valid || points.length < 3) {
    return {
      ok: false,
      points: [],
      reason: "Invalid contour coordinates",
    };
  }

  return {
    ok: true,
    points,
    rawPointCount: raw.length,
    pointCount: points.length,
  };
}

/* =========================================================================
   Preview
   ========================================================================= */

function drawPreview(
  sourceCanvas,
  canvas,
  cells,
  selectedId
) {
  const ctx = canvas.getContext("2d");
  const maxW = 1200;
  const scale = Math.min(
    1,
    maxW / sourceCanvas.width
  );

  canvas.width = Math.round(
    sourceCanvas.width * scale
  );
  canvas.height = Math.round(
    sourceCanvas.height * scale
  );

  ctx.clearRect(
    0,
    0,
    canvas.width,
    canvas.height
  );

  ctx.drawImage(
    sourceCanvas,
    0,
    0,
    canvas.width,
    canvas.height
  );

  for (const cell of cells) {
    const x = cell.x * scale;
    const y = cell.y * scale;
    const w = cell.width * scale;
    const h = cell.height * scale;

    ctx.save();
    ctx.lineWidth =
      selectedId === cell.id ? 4 : 2;
    ctx.strokeStyle =
      selectedId === cell.id
        ? "#00e5ff"
        : "#ff00ff";
    ctx.strokeRect(
      x + 1,
      y + 1,
      w - 2,
      h - 2
    );

    ctx.fillStyle =
      "rgba(255,0,255,0.08)";
    ctx.fillRect(
      x,
      y,
      w,
      h
    );

    ctx.fillStyle = "#111";
    ctx.font = `bold ${Math.max(
      12,
      Math.min(28, w / 12)
    )}px Arial`;

    ctx.fillText(
      `${cell.row + 1},${cell.col + 1}`,
      x + 8,
      y + 24
    );

    ctx.restore();

    if (cell.kissCut?.ok) {
      ctx.save();
      ctx.strokeStyle = "#ff00ff";
      ctx.lineWidth = 1.5;
      ctx.beginPath();

      cell.kissCut.points.forEach(
        ([px, py], i) => {
          const vx =
            (cell.x + px) * scale;
          const vy =
            (cell.y + py) * scale;

          if (i === 0) {
            ctx.moveTo(vx, vy);
          } else {
            ctx.lineTo(vx, vy);
          }
        }
      );

      ctx.closePath();
      ctx.stroke();
      ctx.restore();
    }
  }
}

function cropCell(
  sourceCanvas,
  cell
) {
  const sx = clamp(
    Math.floor(cell.x),
    0,
    sourceCanvas.width
  );
  const sy = clamp(
    Math.floor(cell.y),
    0,
    sourceCanvas.height
  );
  const ex = clamp(
    Math.ceil(cell.x + cell.width),
    sx + 1,
    sourceCanvas.width
  );
  const ey = clamp(
    Math.ceil(cell.y + cell.height),
    sy + 1,
    sourceCanvas.height
  );

  const out =
    document.createElement("canvas");

  out.width = ex - sx;
  out.height = ey - sy;

  out
    .getContext("2d")
    .drawImage(
      sourceCanvas,
      sx,
      sy,
      ex - sx,
      ey - sy,
      0,
      0,
      out.width,
      out.height
    );

  return out;
}

/* =========================================================================
   Main component
   ========================================================================= */

export default function PdfStickerGridDetector() {
  const previewRef = useRef(null);

  const [pdfPages, setPdfPages] =
    useState([]);
  const [pageIndex, setPageIndex] =
    useState(0);

  const [rows, setRows] =
    useState(KNOWN_JSPDF_SHEET.rows);
  const [cols, setCols] =
    useState(KNOWN_JSPDF_SHEET.cols);

  const [selectedCell, setSelectedCell] =
    useState(null);
  const [detecting, setDetecting] =
    useState(false);
  const [exporting, setExporting] =
    useState(false);
  const [error, setError] =
    useState("");

  const [params, setParams] = useState({
    offsetMm: 0,
    smoothing: 0,
    joinDistance: 0,
    bgThreshold: 50,
    dpi: 298,
  });

  const activePage =
    pdfPages[pageIndex];

  const setParam = (key) => (event) => {
    const value = Number(event.target.value);

    setParams((prev) => ({
      ...prev,
      [key]: Number.isFinite(value)
        ? value
        : prev[key],
    }));
  };

  const detectPage = useCallback(
    (page) => {
      if (!page) return;

      setDetecting(true);
      setError("");

      requestAnimationFrame(() => {
        try {
          const grid = inferGrid(
            page.canvas,
            rows,
            cols
          );

          setPdfPages((old) =>
            old.map((p, i) =>
              i === pageIndex
                ? {
                    ...p,
                    grid,
                    contours: null,
                  }
                : p
            )
          );

          setSelectedCell(
            grid.cells[0]?.id || null
          );
        } catch (e) {
          setError(
            `Detection failed: ${
              e?.message || String(e)
            }`
          );
        } finally {
          setDetecting(false);
        }
      });
    },
    [cols, pageIndex, rows]
  );

  useEffect(() => {
    if (
      !activePage ||
      !previewRef.current
    ) {
      return;
    }

    drawPreview(
      activePage.canvas,
      previewRef.current,
      activePage.grid?.cells || [],
      selectedCell
    );
  }, [
    activePage,
    selectedCell,
    params,
  ]);

  const handlePdf = async (file) => {
    if (!file) return;

    setError("");
    setPdfPages([]);
    setPageIndex(0);
    setSelectedCell(null);

    try {
      const bytes =
        new Uint8Array(
          await file.arrayBuffer()
        );

      const pdf =
        await pdfjsLib
          .getDocument({
            data: bytes,
            disableWorker: true,
          })
          .promise;

      const pages = [];

      for (
        let i = 1;
        i <= pdf.numPages;
        i++
      ) {
        const page =
          await pdf.getPage(i);

        const baseViewport =
          page.getViewport({
            scale: 1,
          });

        /*
         * Keep a high-quality raster for contour detection.
         * The PDF export below uses the same rasterized page and
         * the exact original PDF page dimensions.
         */
        const targetWidth = 1800;
        const scale = Math.max(
          1,
          targetWidth /
            baseViewport.width
        );

        const viewport =
          page.getViewport({
            scale,
          });

        const canvas =
          document.createElement(
            "canvas"
          );

        canvas.width =
          Math.ceil(viewport.width);
        canvas.height =
          Math.ceil(viewport.height);

        await page.render({
          canvasContext:
            canvas.getContext("2d", {
              alpha: false,
            }),
          viewport,
        }).promise;

        /*
         * PDF.js page size is in points.
         * Store it so the exported PDF is the same physical size.
         */
        pages.push({
          pageNumber: i,
          canvas,
          width: canvas.width,
          height: canvas.height,
          pageWidthPt:
            baseViewport.width,
          pageHeightPt:
            baseViewport.height,
          renderScale: scale,
          grid: null,
        });
      }

      setPdfPages(pages);

      if (pages[0]) {
        setTimeout(
          () => detectPage(pages[0]),
          0
        );
      }
    } catch (e) {
      setError(
        `PDF could not be loaded: ${
          e?.message || String(e)
        }`
      );
    }
  };

  const handleUpload = (event) => {
    const file =
      event.target.files?.[0];

    handlePdf(file);
    event.target.value = "";
  };

  const redetect = () => {
    if (activePage) {
      detectPage(activePage);
    }
  };

  /*
   * Detect the ACTUAL border/artwork contour in every grid cell.
   * This is deliberately separate from grid detection.
   */
  const detectKissCuts = async () => {
    if (
      !activePage?.grid?.cells?.length
    ) {
      return;
    }

    setDetecting(true);
    setError("");

    try {
      const cells =
        activePage.grid.cells.map(
          (cell) => ({
            ...cell,
            kissCut: null,
          })
        );

      /*
       * Process sequentially to avoid freezing the browser by
       * running 25 contour operations simultaneously.
       */
      for (const cell of cells) {
        const cellCanvas =
          cropCell(
            activePage.canvas,
            cell
          );

        try {
          cell.kissCut =
            detectCellKissCut(
              cellCanvas,
              params
            );
        } catch (e) {
          cell.kissCut = {
            ok: false,
            points: [],
            reason:
              e?.message ||
              "Contour detection failed",
          };
        }

        /*
         * Give the browser a frame between cells.
         */
        await new Promise(
          (resolve) =>
            requestAnimationFrame(
              resolve
            )
        );
      }

      setPdfPages((old) =>
        old.map((p, i) =>
          i === pageIndex
            ? {
                ...p,
                grid: {
                  ...p.grid,
                  cells,
                },
              }
            : p
        )
      );
    } finally {
      setDetecting(false);
    }
  };

  /*
   * Draw the SAME PDF page artwork and then draw every detected contour
   * as a vector magenta line using jsPDF.
   *
   * Coordinates:
   * - source cell coordinates are pixels
   * - PDF page coordinates are points
   * - page Y is converted from browser top-left to PDF top-left
   *   using a transformation matrix, so the visual alignment is exact.
   */
  const downloadKissCutPdf = async () => {
    if (
      !activePage ||
      !activePage.grid?.cells?.length
    ) {
      return;
    }

    const cells =
      activePage.grid.cells;

    const readyCuts =
      cells.filter(
        (c) => c.kissCut?.ok
      );

    if (!readyCuts.length) {
      setError(
        "No kiss-cut contours are ready. Click Detect Kiss Cuts first."
      );
      return;
    }

    setExporting(true);
    setError("");

    try {
      const doc =
        new jsPDF({
          unit: "pt",
          format: [
            activePage.pageWidthPt,
            activePage.pageHeightPt,
          ],
          orientation:
            activePage.pageWidthPt >=
            activePage.pageHeightPt
              ? "landscape"
              : "portrait",
          compress: true,
        });

      /*
       * Put the rasterized source page over the entire PDF page.
       * It is the same visual page that was detected.
       */
      const pageData =
        activePage.canvas.toDataURL(
          "image/jpeg",
          0.95
        );

      doc.addImage(
        pageData,
        "JPEG",
        0,
        0,
        activePage.pageWidthPt,
        activePage.pageHeightPt
      );

      /*
       * Convert source canvas pixels to PDF points.
       *
       * PDF.js render canvas:
       *   canvas.width = pageWidthPt * renderScale
       *
       * Therefore:
       *   ptPerPx = 1 / renderScale
       */
      const ptPerPx =
        1 / activePage.renderScale;

      /*
       * Make the kiss-cut vector thin and magenta.
       * 0.20 mm is the same vector stroke used by StickerKissCutStudio.
       */
      const lineWidthPt =
        (0.20 / 25.4) * 72;

      doc.setDrawColor(
        ...MAGENTA
      );
      doc.setLineWidth(
        lineWidthPt
      );
      doc.setLineCap("round");
      doc.setLineJoin("round");

      /*
       * Draw each contour as separate line segments.
       *
       * IMPORTANT:
       * This does NOT draw the detected grid rectangle.
       * It draws the actual contour returned by the
       * StickerKissCutStudio-style detector.
       */
      for (const cell of readyCuts) {
        const points =
          cell.kissCut.points;

        if (points.length < 3) {
          continue;
        }

        /*
         * Use the same top-left coordinate system as the source image.
         * jsPDF's coordinate system is top-left for normal drawing
         * operations, so no manual Y inversion is necessary here.
         */
        for (
          let i = 0;
          i < points.length;
          i++
        ) {
          const a =
            points[i];
          const b =
            points[
              (i + 1) %
                points.length
            ];

          const x1 =
            (cell.x + a[0]) *
            ptPerPx;
          const y1 =
            (cell.y + a[1]) *
            ptPerPx;
          const x2 =
            (cell.x + b[0]) *
            ptPerPx;
          const y2 =
            (cell.y + b[1]) *
            ptPerPx;

          doc.line(
            x1,
            y1,
            x2,
            y2
          );
        }
      }

      const baseName =
        "kiss-cut-sheet";

      doc.save(
        `${baseName}-page-${activePage.pageNumber}.pdf`
      );
    } catch (e) {
      setError(
        `PDF export failed: ${
          e?.message || String(e)
        }`
      );
    } finally {
      setExporting(false);
    }
  };

  const exportCell = (cell) => {
    if (!activePage || !cell) return;

    const cropped =
      cropCell(
        activePage.canvas,
        cell
      );

    const link =
      document.createElement("a");

    link.download =
      `page-${activePage.pageNumber}-sticker-${cell.row + 1}-${cell.col + 1}.png`;

    link.href =
      cropped.toDataURL(
        "image/png"
      );

    link.click();
  };

  const cellCount =
    activePage?.grid?.cells
      ?.length || 0;

  const cutCount =
    activePage?.grid?.cells?.filter(
      (c) => c.kissCut?.ok
    ).length || 0;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            PDF Sticker Grid +
            Kiss-Cut Detector
          </h1>

          <p style={styles.subtitle}>
            Detect the borders exactly from
            the uploaded sheet and export
            the same page with vector
            magenta kiss-cut lines.
          </p>
        </div>

        <label
          style={styles.uploadButton}
        >
          <FileUp size={18} />
          Upload PDF

          <input
            type="file"
            accept="application/pdf"
            onChange={handleUpload}
            hidden
          />
        </label>
      </div>

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      <div style={styles.controls}>
        <div>
          <label>Rows</label>
          <input
            type="number"
            min="1"
            max="30"
            value={rows}
            onChange={(e) =>
              setRows(
                clamp(
                  Number(
                    e.target.value
                  ) || 1,
                  1,
                  30
                )
              )
            }
          />
        </div>

        <div>
          <label>Columns</label>
          <input
            type="number"
            min="1"
            max="30"
            value={cols}
            onChange={(e) =>
              setCols(
                clamp(
                  Number(
                    e.target.value
                  ) || 1,
                  1,
                  30
                )
              )
            }
          />
        </div>

        <div>
          <label>
            Kiss-cut Offset (mm)
          </label>
          <input
            type="number"
            min="-8"
            max="8"
            step="0.1"
            value={params.offsetMm}
            onChange={setParam(
              "offsetMm"
            )}
          />
        </div>

        <div>
          <label>
            Background Threshold
          </label>
          <input
            type="number"
            min="1"
            max="150"
            step="1"
            value={
              params.bgThreshold
            }
            onChange={setParam(
              "bgThreshold"
            )}
          />
        </div>

        <div>
          <label>
            Smoothing
          </label>
          <input
            type="number"
            min="0"
            max="20"
            step="0.1"
            value={params.smoothing}
            onChange={setParam(
              "smoothing"
            )}
          />
        </div>

        <div>
          <label>
            Join Artwork
          </label>
          <input
            type="number"
            min="0"
            max="30"
            step="1"
            value={
              params.joinDistance
            }
            onChange={setParam(
              "joinDistance"
            )}
          />
        </div>

        <button
          onClick={redetect}
          disabled={
            !activePage ||
            detecting
          }
          style={styles.button}
        >
          <RefreshCw size={16} />
          {detecting
            ? "Detecting..."
            : "Detect Grid"}
        </button>

        <button
          onClick={
            detectKissCuts
          }
          disabled={
            !activePage ||
            !cellCount ||
            detecting
          }
          style={styles.button}
        >
          <ScanLine size={16} />
          {detecting
            ? "Detecting..."
            : "Detect Kiss Cuts"}
        </button>

        <button
          onClick={
            downloadKissCutPdf
          }
          disabled={
            !cutCount ||
            exporting
          }
          style={
            styles.secondaryButton
          }
        >
          <Download size={16} />
          {exporting
            ? "Creating PDF..."
            : "Download Kiss-Cut PDF"}
        </button>
      </div>

      {pdfPages.length > 0 && (
        <div style={styles.pageBar}>
          <button
            onClick={() =>
              setPageIndex(
                (p) =>
                  Math.max(
                    0,
                    p - 1
                  )
              )
            }
            disabled={
              pageIndex === 0
            }
            style={
              styles.smallButton
            }
          >
            Previous
          </button>

          <strong>
            Page{" "}
            {pageIndex + 1} /{" "}
            {pdfPages.length}
          </strong>

          <button
            onClick={() =>
              setPageIndex(
                (p) =>
                  Math.min(
                    pdfPages.length -
                      1,
                    p + 1
                  )
              )
            }
            disabled={
              pageIndex ===
              pdfPages.length - 1
            }
            style={
              styles.smallButton
            }
          >
            Next
          </button>

          <span
            style={styles.count}
          >
            {cellCount} detected cells
            {" • "}
            {cutCount} kiss cuts
          </span>
        </div>
      )}

      <div style={styles.workspace}>
        <div
          style={styles.previewCard}
        >
          {!activePage ? (
            <div style={styles.empty}>
              <Grid3X3 size={52} />

              <h2>
                Upload a sticker-sheet
                PDF
              </h2>

              <p>
                The default detector is
                configured for the current
                jsPDF 5 × 5 sheet:
                460 × 305 mm with
                89 × 57 mm artwork cells.
              </p>
            </div>
          ) : (
            <canvas
              ref={previewRef}
              style={styles.canvas}
            />
          )}
        </div>

        <aside
          style={styles.sidebar}
        >
          <div
            style={
              styles.sidebarTitle
            }
          >
            <ScanLine size={18} />
            Detection
          </div>

          <div style={styles.info}>
            <b>PDF page size</b>

            <p>
              {activePage
                ? (((activePage.pageWidthPt / 72) * 25.4).toFixed(2))
                : "—"}{" "}
              mm ×{" "}
              {activePage
                ? (
                    (activePage.pageHeightPt /
                      72) *
                    25.4
                  ).toFixed(2)
                : "—"}{" "}
              mm
            </p>

            <b>
              Source jsPDF reference
            </b>

            <p>
              460 × 305 mm landscape,
              5 × 5 cells,
              89 × 57 mm per cell.
            </p>
          </div>

          {activePage?.grid && (
            <>
              <div style={styles.stat}>
                <span>Rows</span>
                <b>
                  {
                    activePage.grid
                      .rows
                  }
                </b>
              </div>

              <div style={styles.stat}>
                <span>Columns</span>
                <b>
                  {
                    activePage.grid
                      .cols
                  }
                </b>
              </div>

              <div style={styles.stat}>
                <span>Detected cells</span>
                <b>
                  {
                    activePage.grid
                      .cells
                      .length
                  }
                </b>
              </div>

              <div style={styles.stat}>
                <span>Kiss cuts</span>
                <b>{cutCount}</b>
              </div>
            </>
          )}

          {selectedCell &&
            activePage?.grid && (
              <div
                style={
                  styles.selected
                }
              >
                Selected:{" "}
                <b>
                  {(() => {
                    const c =
                      activePage.grid.cells.find(
                        (x) =>
                          x.id ===
                          selectedCell
                      );

                    return c
                      ? `Row ${
                          c.row + 1
                        }, Column ${
                          c.col + 1
                        }`
                      : selectedCell;
                  })()}
                </b>
              </div>
            )}

          {activePage?.grid?.cells
            ?.length > 0 && (
            <button
              style={
                styles.secondaryButton
              }
              onClick={() =>
                exportCell(
                  activePage.grid.cells.find(
                    (c) =>
                      c.id ===
                      selectedCell
                  )
                )
              }
            >
              <Download size={16} />
              Export Selected PNG
            </button>
          )}

          <button
            style={
              styles.clearButton
            }
            onClick={() => {
              setPdfPages([]);
              setSelectedCell(null);
              setError("");
            }}
          >
            <Trash2 size={16} />
            Clear
          </button>
        </aside>
      </div>

      {activePage?.grid?.cells
        ?.length > 0 && (
        <div
          style={styles.cellList}
        >
          {activePage.grid.cells.map(
            (cell) => (
              <button
                key={cell.id}
                onClick={() =>
                  setSelectedCell(
                    cell.id
                  )
                }
                style={{
                  ...styles.cellButton,
                  ...(selectedCell ===
                  cell.id
                    ? styles.cellButtonActive
                    : {}),
                }}
              >
                {cell.row + 1},
                {cell.col + 1}
                {cell.kissCut?.ok
                  ? " ✓"
                  : ""}
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#0d0d12",
    color: "#f5f5f7",
    padding: 24,
    boxSizing: "border-box",
    fontFamily:
      "Arial, sans-serif",
  },

  header: {
    display: "flex",
    justifyContent:
      "space-between",
    alignItems: "center",
    gap: 20,
    marginBottom: 20,
  },

  title: {
    margin: 0,
    fontSize: 26,
  },

  subtitle: {
    margin: "6px 0 0",
    color: "#a8a8b3",
  },

  uploadButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 18px",
    borderRadius: 10,
    background: "#b000b5",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 700,
  },

  controls: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "end",
    gap: 12,
    padding: 16,
    background: "#181820",
    border: "1px solid #2c2c36",
    borderRadius: 12,
    marginBottom: 16,
  },

  button: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    border: 0,
    borderRadius: 9,
    padding: "10px 15px",
    background: "#b000b5",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },

  secondaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent:
      "center",
    gap: 7,
    border:
      "1px solid #454550",
    borderRadius: 9,
    padding: "10px 15px",
    background: "#24242d",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
    width: "100%",
    marginTop: 10,
  },

  smallButton: {
    border:
      "1px solid #454550",
    borderRadius: 7,
    padding: "7px 11px",
    background: "#24242d",
    color: "#fff",
    cursor: "pointer",
  },

  clearButton: {
    marginTop: 12,
    width: "100%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent:
      "center",
    gap: 7,
    border:
      "1px solid #6b3030",
    borderRadius: 9,
    padding: "10px 15px",
    background: "#301b1b",
    color: "#ffb8b8",
    fontWeight: 700,
    cursor: "pointer",
  },

  pageBar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 14,
  },

  count: {
    marginLeft: "auto",
    color: "#b7b7c4",
  },

  workspace: {
    display: "grid",
    gridTemplateColumns:
      "minmax(0, 1fr) 260px",
    gap: 16,
  },

  previewCard: {
    minHeight: 500,
    display: "flex",
    alignItems: "center",
    justifyContent:
      "center",
    overflow: "auto",
    padding: 12,
    background: "#17171e",
    border:
      "1px solid #30303a",
    borderRadius: 12,
  },

  canvas: {
    display: "block",
    maxWidth: "100%",
    height: "auto",
    cursor: "crosshair",
  },

  empty: {
    textAlign: "center",
    color: "#9f9fac",
    padding: 60,
  },

  sidebar: {
    background: "#181820",
    border:
      "1px solid #30303a",
    borderRadius: 12,
    padding: 16,
  },

  sidebarTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 18,
    fontWeight: 800,
    marginBottom: 16,
  },

  info: {
    background: "#22222b",
    padding: 12,
    borderRadius: 9,
    color: "#bdbdc8",
    lineHeight: 1.45,
    marginBottom: 14,
  },

  stat: {
    display: "flex",
    justifyContent:
      "space-between",
    padding: "10px 0",
    borderBottom:
      "1px solid #30303a",
    color: "#b7b7c4",
  },

  selected: {
    marginTop: 14,
    padding: 10,
    borderRadius: 8,
    background:
      "rgba(255,0,255,.10)",
    color: "#eee",
  },

  cellList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 14,
    padding: 12,
    background: "#181820",
    border:
      "1px solid #30303a",
    borderRadius: 12,
  },

  cellButton: {
    minWidth: 42,
    padding: "7px 9px",
    border:
      "1px solid #41414c",
    borderRadius: 6,
    background: "#22222b",
    color: "#cfcfd8",
    cursor: "pointer",
  },

  cellButtonActive: {
    background: "#b000b5",
    color: "#fff",
    borderColor:
      "#e000e8",
  },

  error: {
    marginBottom: 16,
    padding: 12,
    borderRadius: 9,
    background: "#3b1919",
    border:
      "1px solid #8b3a3a",
    color: "#ffc1c1",
  },
};
